import { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import MaterialForm from '../components/MaterialForm';
import MaterialPresetSelector from '../components/MaterialPresetSelector';
import { useToast } from '../hooks/useToast';
import { getDisplayName } from '../utils/i18n';

export default function Materials() {
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPresetSelector, setShowPresetSelector] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    showInSite: 'all'
  });
  const { showToast } = useToast();

  useEffect(() => {
    fetchMaterials();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, filters]);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/materials');
      setMaterials(response.data.data);
    } catch (err) {
      setError('Failed to fetch materials');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    console.log('materials', materials);
    let filtered = [...materials];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(m =>
        getDisplayName(m.name).toLowerCase().includes(search) ||
        m.key?.toLowerCase().includes(search)
      );
    }

    if (filters.showInSite !== 'all') {
      filtered = filtered.filter(m => m.showInSite === (filters.showInSite === 'true'));
    }

    // Sort by displayOrder for proper reordering
    filtered.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    setFilteredMaterials(filtered);
  };

  const handleAdd = () => {
    setEditingMaterial(null);
    setShowPresetSelector(true);
  };

  const handleSelectPreset = (duplicatedMaterial) => {
    setShowPresetSelector(false);
    setEditingMaterial(duplicatedMaterial);
    setShowForm(true);
    showToast('Material created from preset. You can now customize it.', 'success');
  };

  const handleCreateFromScratch = () => {
    setShowPresetSelector(false);
    setEditingMaterial(null);
    setShowForm(true);
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setShowForm(true);
  };

  const handleDelete = async (material) => {
    if (!window.confirm(`Are you sure you want to delete "${material.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/auth/materials/${material._id}`);
      fetchMaterials();
    } catch (err) {
      alert('Failed to delete material');
      console.error(err);
    }
  };

  const handleDuplicate = async (material) => {
    try {
      const response = await api.post(`/auth/materials/${material._id}/duplicate?from=farm`);
      setEditingMaterial(response.data.data);
      setShowForm(true);
      showToast('Material duplicated. You can now customize it.', 'success');
    } catch (err) {
      alert('Failed to duplicate material');
      console.error(err);
    }
  };

  const handleSave = async (data) => {
    const isEditing = !!editingMaterial;
    const isFormData = data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    if (isEditing) {
      await api.patch(`/auth/materials/${editingMaterial._id}`, data, config);
    } else {
      await api.post('/auth/materials', data, config);
    }
    setShowForm(false);
    setEditingMaterial(null);
    fetchMaterials();
    showToast(isEditing ? 'Material updated successfully!' : 'Material created successfully!', 'success');

  };

  const handleReorder = async (affectedItems) => {
    try {
      await api.patch('/auth/materials/reorder', affectedItems);
      // Update local state optimistically
      const updatedMaterials = materials.map(material => {
        const affected = affectedItems.find(item => item.id === material._id);
        if (affected) {
          return { ...material, displayOrder: affected.displayOrder };
        }
        return material;
      });
      setMaterials(updatedMaterials);
    } catch (err) {
      alert('Failed to reorder materials');
      console.error(err);
      fetchMaterials(); // Revert to server state on error
    }
  };

  const columns = [
    { key: 'name', label: 'Name', render: (val) => getDisplayName(val) },
    { key: 'key', label: 'Key' },
    {
      key: 'pricePerPrintedKg',
      label: 'Price/Kg (€)',
      render: (val) => val ? `€${Number(val).toFixed(2)}` : '€1.00'
    },
    {
      key: 'showInSite',
      label: 'Show in Site',
      render: (val) => (val ? '✓' : '✗')
    },
    { key: 'displayOrder', label: 'Order' },
    {
      key: 'createdAt',
      label: 'Created',
      render: (val) => new Date(val).toLocaleDateString()
    }
  ];

  const renderFilters = () => (
    <div className="flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        placeholder="Search by name or key..."
        value={filters.search}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      <select
        value={filters.showInSite}
        onChange={(e) => setFilters({ ...filters, showInSite: e.target.value })}
        className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
      >
        <option value="all">All Visibility</option>
        <option value="true">Show in Site</option>
        <option value="false">Hidden from Site</option>
      </select>
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Loading materials...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
      {error}
    </div>
  );

  return (
    <div>
      {showPresetSelector ? (
        <MaterialPresetSelector
          onSelectPreset={handleSelectPreset}
          onCreateNew={handleCreateFromScratch}
          onCancel={() => setShowPresetSelector(false)}
        />
      ) : showForm ? (
        <MaterialForm
          material={editingMaterial}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingMaterial(null);
          }}
        />
      ) : (
        <DataTable
          data={filteredMaterials}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onAdd={handleAdd}
          renderFilters={renderFilters}
          reorderable={true}
          onReorder={handleReorder}
        />
      )}
    </div>
  );
}
